/*!
 * bz-game-i18n.js — market string sets for The Focus Challenge
 * Session 32D · projects/briefs/focus-game-2026-08-04/tactics.md
 *
 * ⚠ DE + FR copy is a DRAFT written by Claude. It has NOT had native review and has NOT been
 *   through check_copy_against_canonical.py for those markets. Do not ship without both.
 *
 * COMPLIANCE RULES ENCODED HERE (see tactics.md §2, §4, §7, §14, §15):
 *  - Science strings describe the PHENOMENON, never the player's deficiency.
 *  - No claim vocabulary anywhere in game copy. No product efficacy language.
 *  - "nootropic"/"Nootropikum"/"nootropique" are BANNED on this surface in ALL markets
 *    (restricted surface — Flash QC HIGH). Asserted at load by assertNoLockedTerms().
 *  - No score→product connective copy ("based on your result" etc.) — asserted by
 *    BZGame.assertNoConnective() at render time.
 *  - "IQ"/"QI" deliberately absent from DE + FR (reads as clickbait/scam in both markets).
 *  - DE result/product copy is INTENTIONALLY STUBBED — German claims work is on hold
 *    (Calum 2026-08-04). The game + science copy is safe to build; the product-routing copy
 *    is not, because it depends on the on-hold mood/stress lexicon.
 */
(function (root) {
  'use strict';

  var I18N = {

    /* ============================================================== UK (.com) */
    uk: {
      lang: 'en-GB',
      // STRIP VERSION 1 — social comparison. Calum chose V1 2026-08-06.
      // NO duration claim anywhere in V1: the old V3 string promised "60 seconds" for a game that
      // runs 90-110s, and the round count is still provisional. V1 says "ten rounds", which is
      // true and stays true. A number in customer copy is a promise — do not reintroduce a
      // duration until the round count is settled AND measured.
      barString: 'Are you faster than the average Brit? ›',
      ui: {
        ready: 'Ready?', go: 'GO',
        tapWhenGreen: 'Tap the moment it turns green',
        tooSoon: 'Too soon — wait for green',
        tapChanged: 'Tap the square that keeps changing',
        whichColour: 'Tap the COLOUR of the ink, not the word',
        whichShape: 'Tap the SHAPE you see, not the word',
        repeatSeq: 'Repeat the sequence',
        quickMaths: 'Quick maths',
        round: 'Round', of: 'of', next: 'Next',
        skip: 'Skip this round', untimed: 'Untimed mode',
        playAgain: 'Play again', yourScore: 'Your score',
        stopInZone: 'Stop the marker inside the yellow zone', stop: 'STOP',
        oddOneOut: 'Tap the odd one out',
        byColour: 'BY COLOUR', byShape: 'BY SHAPE',
        countBlue: 'Watch for the BLUE ones', howManyBlue: 'How many blue did you see?',
        howMany: 'How many dots?',
        matchColour: 'Answer by COLOUR — ignore which side it appears on',
        tapMiddle: 'Tap the exact middle of the line',
        rememberWords: 'Remember these words', wasItShown: 'Was this word shown?',
        seenIt: 'I saw it', newWord: 'New'
      },
      roundNames: {
        reaction: 'Reaction speed', subitise: 'Count the dots', oddone: 'Odd one out',
        sequence: 'Repeat the sequence', simon: 'Go by colour', stroop: 'Name the ink',
        timing: 'Stop the marker', words: 'Remember these words',
        switch: 'Switch the rule', blink: 'Count the blue ones'
      },
      howto: {
        reaction: 'A grey box turns green. Tap it the moment it does \u2014 as fast as you can. Two goes.',
        subitise: 'Dots flash up for a moment. Tap how many you saw.',
        oddone:   'One item in the grid is different from the rest. Tap it. Two grids \u2014 the first is meant to be easy, the second is not. The gap between them is the point.',
        sequence: 'Tiles light up in order. Tap them back in the same order. It gets one longer every time you get it right, and ends the moment you slip \u2014 how far you get IS the score.',
        simon:    'A coloured square appears on the left or the right. Tap the button that matches its COLOUR \u2014 ignore which side it is on.',
        stroop:   'A colour word appears, printed in coloured ink. Tap the COLOUR OF THE INK, not the word itself.',
        timing:   'A marker sweeps back and forth. Press STOP while it is inside the yellow zone. It speeds up each go.',
        switch:   'A shape appears. The label above tells you which rule is running: answer BY COLOUR or BY SHAPE. The two buttons change to match the rule \u2014 watch the label, it keeps switching.',
        blink:    'Shapes flash past fast. Watch for the BLUE ones — there may be 1, 2 or 3. Count how many you see \u2014 they can come very close together.',
        words:    'Five words appear one at a time. Remember them. Then you will be asked whether certain words were shown.'
      },
      // No offer exists (removed 2026-08-06).
      offerTerms: null,
      // No reward exists (removed 2026-08-09).
      reward: null,
      // Cold-start: retained as the fallback when no population figure is available at all.
      percentilePending: 'We’re still building the baseline for this one — here’s your score.',
      // Calum 2026-08-06 OVERRULED the N>=200 suppression (§14.2 #1): show the comparison from
      // ZERO and state the population size openly. Transparency about n is what makes a small-n
      // percentile honest — the failure mode was a confident percentile over a hidden, practised
      // internal sample. {pct} and {n} are substituted by the caller.
      percentileOf: 'Faster than {pct}% of the {n} people who have played so far.',
      percentileFirst: 'You’re the first to play this one. Come back when a few more have — that’s when the comparison starts to mean something.',
      spreadSlowLabel: 'Slower ←',
      spreadFastLabel: '→ Faster',
      scaleScoreTitle: 'Score', scaleSpeedTitle: 'Speed',
      scoreLowLabel: 'Lower ←', scoreHighLabel: '→ Higher',
      scoreOf: 'Scored higher than {pct}% of the {n} people who have played so far.',
      // Segue (score -> preference question -> product -> checkout). Shaped to the DOTTED
      // key contract in bz-game-segue.js REQUIRED_STRING_KEYS; that module fails closed on
      // a missing key, so a half-translated market throws instead of rendering a blank
      // button on the highest-intent screen in the flow.
      // Routing is the PREFERENCE answer, never the score — whyThis says so explicitly.
      segue: {
        "intro": "One more question — nothing to do with the game.",
        "seeMyPick": "Choose my formula",
        "question": "What kind of day are you after?",
        "questionHint": "Pick either — or both.",
        "pushLabel": "More push",
        "pushBody": "I want to get going and stay going.",
        "calmLabel": "Less noise",
        "calmBody": "I want things to settle so I can think straight.",
        "pickPrompt": "Pick at least one to see your formula.",
        "continueCta": "Show me",
        "changeAnswer": "Change my answer",
        "pickHeading": "Your pick",
        "whyThis": "Your pick decides this — nothing else.",
        "buyOnce": "Buy once",
        "buyOnceNote": "A one-off order.",
        "subscribe": "Subscribe & Save",
        "subscribeNote": "Skip or cancel anytime.",
        "products": {
          "pro": {
            "tier": "motivation",
            "line": "Strong focus and motivation for the days that need pushing through."
          },
          "elite": {
            "tier": "stress relief",
            "line": "Stress-free focus and a positive mood when everything feels loud."
          },
          "bundle": {
            "tier": "all three formulas",
            "line": "One of each — try all three and find the one that suits you."
          }
        }
      },
      // The player's OWN number, prepended to the science card. Localised because it is
      // customer-facing prose, not a debug label — it used to be hardcoded English.
      stat: {
        reaction:   '{ms}ms.',
        stroop:     '{ms}ms slower when the word fought the ink.',
        sequence:   '{n} in order.',
        simon:      '{ms}ms slower on conflict.',
        switch:     '{ms}ms lost on every rule change.',
        timing:     'Off by {pct}% of the track.',
        bisect:     'Off centre by {pct}%.',
        subitise:   '{pct}% right.',
        oddone:     '{pct}% right.',
        blink:      '{pct}% right.',
        words:      '{pct}% right.'
      },
      science: {
        // COMPARATIVE cards (Calum 2026-08-07). Each names the PUBLISHED effect size so the
        // player's own number, prepended by measuredStat(), has something real to sit against.
        // Every figure is an established, replicated finding, hedged because these effects have
        // ranges rather than point values. The insight stays in the PHENOMENON — the player
        // shares the effect, they are never ranked by it.
        reaction:   'A simple reaction takes most people around 250 milliseconds, and almost none of it is thinking — the signal has to cross eye, brain and thumb. Nobody gets under about 100.',
        subitise:   'Up to about three or four, you do not count — you just see how many. Past that the brain switches to counting one at a time, and the time it takes jumps. That switch is called subitising, and everyone has it.',
        oddone:     'When the odd one differs by a single feature it pops out, and the size of the grid barely matters. When it takes two features combined, you have to search item by item. Same grid, completely different job.',
        sequence:   'Short-term memory holds about seven items, give or take two — the most cited number in psychology. For positions in space it is usually nearer five.',
        simon:      'People are about 30 milliseconds slower when a thing appears on the opposite side to the button it needs. The location is irrelevant to the task and your brain uses it anyway. That is the Simon effect.',
        stroop:     'Reading is so automatic that the word interferes with naming the ink — most people lose about 100 milliseconds to it. You cannot switch it off, which is exactly why it is the most famous task in the field.',
        switch:     'Changing rule costs most people somewhere around 200 to 300 milliseconds, every single time. That is the real price of multitasking: not doing two things at once, but paying a toll each time you swap.',
        timing:     'Judging a moving interval is systematically off for almost everyone, and the faster it moves the more it drifts. Your body keeps time far better than your estimate of it does.',
        blink:      'Spot one target and, for roughly the next 200 to 500 milliseconds, a second one can go past unseen — even in plain view. It is called the attentional blink, and it is not your eyes.',
        words:      'The brain stores the gist, not the recording. A word that fits the theme can feel just as remembered as one you actually saw — a false memory produced by a healthy memory working normally.'
      }
    },

    /* =============================================================== US (.us) */
    us: {
      lang: 'en-US',
      barString: 'Are you faster than the average American? ›',   // V1 — see uk.barString
      ui: {
        ready: 'Ready?', go: 'GO',
        tapWhenGreen: 'Tap the moment it turns green',
        tooSoon: 'Too soon — wait for green',
        tapChanged: 'Tap the square that keeps changing',
        whichColour: 'Tap the COLOR of the ink, not the word',
        whichShape: 'Tap the SHAPE you see, not the word',
        repeatSeq: 'Repeat the sequence',
        quickMaths: 'Quick math',                 // US: "math", not "maths"
        round: 'Round', of: 'of', next: 'Next',
        skip: 'Skip this round', untimed: 'Untimed mode',
        playAgain: 'Play again', yourScore: 'Your score',
        stopInZone: 'Stop the marker inside the yellow zone', stop: 'STOP',
        oddOneOut: 'Tap the odd one out',
        byColour: 'BY COLOR', byShape: 'BY SHAPE',
        countBlue: 'Watch for the BLUE ones', howManyBlue: 'How many blue did you see?',
        howMany: 'How many dots?',
        matchColour: 'Answer by COLOR — ignore which side it appears on',
        tapMiddle: 'Tap the exact middle of the line',
        rememberWords: 'Remember these words', wasItShown: 'Was this word shown?',
        seenIt: 'I saw it', newWord: 'New'
      },
      roundNames: {
        reaction: 'Reaction time', subitise: 'Count the dots', oddone: 'Odd one out',
        sequence: 'Repeat the sequence', simon: 'Go by color', stroop: 'Name the ink',
        timing: 'Stop the marker', words: 'Remember these words',
        switch: 'Switch the rule', blink: 'Count the blue ones'
      },
      howto: {
        reaction: 'A gray box turns green. Tap it the moment it does \u2014 as fast as you can. Two tries.',
        subitise: 'Dots flash up for a moment. Tap how many you saw.',
        oddone:   'One item in the grid is different from the rest. Tap it. Two grids \u2014 the first is meant to be easy, the second is not. The gap between them is the point.',
        sequence: 'Tiles light up in order. Tap them back in the same order. It gets one longer every time you get it right, and ends the moment you slip \u2014 how far you get IS the score.',
        simon:    'A colored square appears on the left or the right. Tap the button that matches its COLOR \u2014 ignore which side it is on.',
        stroop:   'A color word appears, printed in colored ink. Tap the COLOR OF THE INK, not the word itself.',
        timing:   'A marker sweeps back and forth. Press STOP while it is inside the yellow zone. It speeds up each go.',
        switch:   'A shape appears. The label above tells you which rule is running: answer BY COLOR or BY SHAPE. The two buttons change to match the rule \u2014 watch the label, it keeps switching.',
        blink:    'Shapes flash past fast. Watch for the BLUE ones — there may be 1, 2 or 3. Count how many you see \u2014 they can come very close together.',
        words:    'Five words appear one at a time. Remember them. Then you will be asked whether certain words were shown.'
      },
      // No offer exists (removed 2026-08-06).
      offerTerms: null,
      // No reward exists (removed 2026-08-09).
      reward: null,
      percentilePending: 'We’re still building the baseline for this one — here’s your score.',
      percentileOf: 'Faster than {pct}% of the {n} people who have played so far.',
      percentileFirst: 'You’re the first to play this one. Come back when a few more have — that’s when the comparison starts to mean something.',
      spreadSlowLabel: 'Slower ←',
      spreadFastLabel: '→ Faster',
      scaleScoreTitle: 'Score', scaleSpeedTitle: 'Speed',
      scoreLowLabel: 'Lower ←', scoreHighLabel: '→ Higher',
      scoreOf: 'Scored higher than {pct}% of the {n} people who have played so far.',
      // Segue (score -> preference question -> product -> checkout). Shaped to the DOTTED
      // key contract in bz-game-segue.js REQUIRED_STRING_KEYS; that module fails closed on
      // a missing key, so a half-translated market throws instead of rendering a blank
      // button on the highest-intent screen in the flow.
      // Routing is the PREFERENCE answer, never the score — whyThis says so explicitly.
      segue: {
        "intro": "One more question — nothing to do with the game.",
        "seeMyPick": "Choose my formula",
        "question": "What kind of day are you after?",
        "questionHint": "Pick either — or both.",
        "pushLabel": "More push",
        "pushBody": "I want to get going and stay going.",
        "calmLabel": "Less noise",
        "calmBody": "I want things to settle so I can think straight.",
        "pickPrompt": "Pick at least one to see your formula.",
        "continueCta": "Show me",
        "changeAnswer": "Change my answer",
        "pickHeading": "Your pick",
        "whyThis": "Your pick decides this — nothing else.",
        "buyOnce": "Buy once",
        "buyOnceNote": "A one-time order.",
        "subscribe": "Subscribe & Save",
        "subscribeNote": "Skip or cancel anytime.",
        "products": {
          "pro": {
            "tier": "motivation",
            "line": "Strong focus and motivation for the days that need pushing through."
          },
          "elite": {
            "tier": "stress relief",
            "line": "Stress-free focus and a positive mood when everything feels loud."
          },
          "bundle": {
            "tier": "all three formulas",
            "line": "One of each — try all three and find the one that suits you."
          }
        }
      },
      // The player's OWN number, prepended to the science card. Localised because it is
      // customer-facing prose, not a debug label — it used to be hardcoded English.
      stat: {
        reaction:   '{ms}ms.',
        stroop:     '{ms}ms slower when the word fought the ink.',
        sequence:   '{n} in order.',
        simon:      '{ms}ms slower on conflict.',
        switch:     '{ms}ms lost on every rule change.',
        timing:     'Off by {pct}% of the track.',
        bisect:     'Off centre by {pct}%.',
        subitise:   '{pct}% right.',
        oddone:     '{pct}% right.',
        blink:      '{pct}% right.',
        words:      '{pct}% right.'
      },
      science: {
        // COMPARATIVE cards (Calum 2026-08-07). Each names the PUBLISHED effect size so the
        // player's own number, prepended by measuredStat(), has something real to sit against.
        // Every figure is an established, replicated finding, hedged because these effects have
        // ranges rather than point values. The insight stays in the PHENOMENON — the player
        // shares the effect, they are never ranked by it.
        reaction:   'A simple reaction takes most people around 250 milliseconds, and almost none of it is thinking — the signal has to cross eye, brain and thumb. Nobody gets under about 100.',
        subitise:   'Up to about three or four, you do not count — you just see how many. Past that the brain switches to counting one at a time, and the time it takes jumps. That switch is called subitising, and everyone has it.',
        oddone:     'When the odd one differs by a single feature it pops out, and the size of the grid barely matters. When it takes two features combined, you have to search item by item. Same grid, completely different job.',
        sequence:   'Short-term memory holds about seven items, give or take two — the most cited number in psychology. For positions in space it is usually nearer five.',
        simon:      'People are about 30 milliseconds slower when a thing appears on the opposite side to the button it needs. The location is irrelevant to the task and your brain uses it anyway. That is the Simon effect.',
        stroop:     'Reading is so automatic that the word interferes with naming the ink — most people lose about 100 milliseconds to it. You cannot switch it off, which is exactly why it is the most famous task in the field.',
        switch:     'Changing rule costs most people somewhere around 200 to 300 milliseconds, every single time. That is the real price of multitasking: not doing two things at once, but paying a toll each time you swap.',
        timing:     'Judging a moving interval is systematically off for almost everyone, and the faster it moves the more it drifts. Your body keeps time far better than your estimate of it does.',
        blink:      'Spot one target and, for roughly the next 200 to 500 milliseconds, a second one can go past unseen — even in plain view. It is called the attentional blink, and it is not your eyes.',
        words:      'The brain stores the gist, not the recording. A word that fits the theme can feel just as remembered as one you actually saw — a false memory produced by a healthy memory working normally.'
      }
    },

    /* =============================================================== DE (.de) */
    /* ⚠ Game + science copy only. Product/result copy is STUBBED — German claims work is on
       hold (Calum 2026-08-04). Do not author DE result copy until that hold lifts.
       "IQ-Test" and "Gewinnspiel" deliberately absent. */
    de: {
      lang: 'de-DE',
      // V1 — see uk.barString. `abschneiden` is the standard German "how did I do"; `im Vergleich`
      // echoes verbatim into the landing headline, so the pair links as tightly as the English.
      barString: 'Wie schneidest du im Vergleich ab? ›',
      ui: {
        ready: 'Bereit?', go: 'LOS',
        tapWhenGreen: 'Tippe, sobald es grün wird',
        tooSoon: 'Zu früh — warte auf Grün',
        tapChanged: 'Tippe auf das Feld, das sich ständig ändert',
        whichColour: 'Tippe auf die FARBE der Schrift, nicht auf das Wort',
        whichShape: 'Tippe auf die FORM, die du siehst, nicht auf das Wort',
        repeatSeq: 'Wiederhole die Reihenfolge',
        quickMaths: 'Kopfrechnen',
        round: 'Runde', of: 'von', next: 'Weiter',
        skip: 'Diese Runde überspringen', untimed: 'Ohne Zeitmessung',
        playAgain: 'Nochmal spielen', yourScore: 'Dein Ergebnis',
        stopInZone: 'Stoppe den Balken im gelben Bereich', stop: 'STOPP',
        oddOneOut: 'Tippe auf das, was nicht passt',
        byColour: 'NACH FARBE', byShape: 'NACH FORM',
        countBlue: 'Achte auf die BLAUEN', howManyBlue: 'Wie viele blaue hast du gesehen?',
        howMany: 'Wie viele Punkte?',
        matchColour: 'Antworte nach der FARBE — ignoriere die Seite',
        tapMiddle: 'Tippe genau auf die Mitte der Linie',
        rememberWords: 'Merke dir diese Wörter', wasItShown: 'War dieses Wort dabei?',
        seenIt: 'Ja, war dabei', newWord: 'Neu'
      },
      roundNames: {
        reaction: 'Schnell reagieren', subitise: 'Punkte z\u00e4hlen', oddone: 'Was passt nicht?',
        sequence: 'Reihenfolge merken', simon: 'Nur die Farbe z\u00e4hlt', stroop: 'Farbe, nicht Wort',
        timing: 'Balken stoppen', words: 'Wortged\u00e4chtnis',
        switch: 'Regel wechseln', blink: 'Blaue Formen z\u00e4hlen'
      },
      howto: {
        reaction: 'Eine graue Fl\u00e4che wird gr\u00fcn. Tippe sofort darauf \u2014 so schnell du kannst. Zwei Durchg\u00e4nge.',
        subitise: 'Punkte erscheinen kurz. Tippe an, wie viele du gesehen hast.',
        oddone:   'Ein Element im Raster unterscheidet sich. Tippe es an. Zwei Raster \u2014 das erste ist absichtlich leicht, das zweite nicht. Der Unterschied ist der Punkt.',
        sequence: 'Felder leuchten der Reihe nach auf. Tippe sie in derselben Reihenfolge nach. Bei jedem Treffer wird die Reihe l\u00e4nger \u2014 beim ersten Fehler ist Schluss. Wie weit du kommst, IST das Ergebnis.',
        simon:    'Ein farbiges Feld erscheint links oder rechts. Tippe die Taste mit der passenden FARBE \u2014 die Seite ist egal.',
        stroop:   'Ein Farbwort erscheint in farbiger Schrift. Tippe die FARBE DER SCHRIFT an, nicht das Wort.',
        timing:   'Ein Balken l\u00e4uft hin und her. Dr\u00fccke STOPP, w\u00e4hrend er im gelben Bereich ist. Er wird schneller.',
        switch:   'Eine Form erscheint. Die Beschriftung oben sagt, welche Regel gilt: NACH FARBE oder NACH FORM. Die beiden Tasten wechseln mit der Regel \u2014 achte auf die Beschriftung.',
        blink:    'Formen blitzen schnell auf. Achte auf die BLAUEN — es können 1, 2 oder 3 sein. Z\u00e4hle, wie viele blaue du siehst \u2014 sie k\u00f6nnen sehr dicht aufeinander folgen.',
        words:    'F\u00fcnf W\u00f6rter erscheinen nacheinander. Merke sie dir. Danach wirst du gefragt, ob bestimmte W\u00f6rter dabei waren.'
      },
      offerTerms: null,          // STUBBED — see header note
      reward: null,              // STUBBED — the offer copy depends on the on-hold DE lexicon
      percentilePending: 'Wir sammeln noch Vergleichswerte — hier ist dein Ergebnis.',
      percentileOf: 'Schneller als {pct}% der bisher {n} Teilnehmenden.',
      percentileFirst: 'Du bist die erste Person, die mitgemacht hat. Komm wieder, wenn ein paar mehr dabei sind — dann wird der Vergleich aussagekräftig.',
      spreadSlowLabel: 'Langsamer ←',
      spreadFastLabel: '→ Schneller',
      scaleScoreTitle: 'Ergebnis', scaleSpeedTitle: 'Tempo',
      scoreLowLabel: 'Niedriger ←', scoreHighLabel: '→ Höher',
      scoreOf: 'Ein höheres Ergebnis als {pct}% der bisher {n} Teilnehmenden.',
      // Segue (score -> preference question -> product -> checkout). Shaped to the DOTTED
      // key contract in bz-game-segue.js REQUIRED_STRING_KEYS; that module fails closed on
      // a missing key, so a half-translated market throws instead of rendering a blank
      // button on the highest-intent screen in the flow.
      // Routing is the PREFERENCE answer, never the score — whyThis says so explicitly.
      segue: {
        "intro": "Noch eine Frage — sie hat nichts mit dem Spiel zu tun.",
        "seeMyPick": "Meine Formel wählen",
        "question": "Wie soll dein Tag laufen?",
        "questionHint": "Wähle eins — oder beides.",
        "pushLabel": "Mehr Antrieb",
        "pushBody": "Ich will in Gang kommen und in Gang bleiben.",
        "calmLabel": "Weniger Lärm im Kopf",
        "calmBody": "Ich will, dass sich alles beruhigt, damit ich klar denken kann.",
        "pickPrompt": "Wähle mindestens eins, um deine Formel zu sehen.",
        "continueCta": "Zeig mir",
        "changeAnswer": "Auswahl ändern",
        "pickHeading": "Deine Auswahl",
        "whyThis": "Das ergibt sich aus deiner Auswahl — aus sonst nichts.",
        "buyOnce": "Einmalig kaufen",
        "buyOnceNote": "Eine einmalige Bestellung.",
        "subscribe": "Abonnieren & Sparen",
        "subscribeNote": "Jederzeit pausieren oder kündigen.",
        "products": {
          "pro": {
            "tier": "Starker Fokus",
            "line": "Starker Fokus und Konzentration für lange, fordernde Tage."
          },
          "elite": {
            "tier": "Klarer Fokus",
            "line": "Klarer Fokus, wenn es um dich herum laut wird."
          },
          "bundle": {
            "tier": "alle drei Formeln",
            "line": "Von jeder eine — probier alle drei und finde deine."
          }
        }
      },
      // The player's OWN number, prepended to the science card. Localised because it is
      // customer-facing prose, not a debug label — it used to be hardcoded English.
      stat: {
        reaction:   '{ms} ms.',
        stroop:     '{ms} ms langsamer, als das Wort gegen die Farbe arbeitete.',
        sequence:   '{n} in der richtigen Reihenfolge.',
        simon:      '{ms} ms langsamer im Konflikt.',
        switch:     '{ms} ms Verlust bei jedem Regelwechsel.',
        timing:     'Um {pct}% der Strecke daneben.',
        bisect:     'Um {pct}% neben der Mitte.',
        subitise:   '{pct}% richtig.',
        oddone:     '{pct}% richtig.',
        blink:      '{pct}% richtig.',
        words:      '{pct}% richtig.'
      },
      science: {
        // COMPARATIVE cards (Calum 2026-08-07). Each names the PUBLISHED effect size so the
        // player's own number, prepended by measuredStat(), has something real to sit against.
        // Every figure is an established, replicated finding, hedged because these effects have
        // ranges rather than point values. The insight stays in the PHENOMENON — the player
        // shares the effect, they are never ranked by it.
        reaction:   'Eine einfache Reaktion dauert bei den meisten Menschen rund 250 Millisekunden, und fast nichts davon ist Denken — das Signal muss von Auge über Gehirn zum Daumen. Unter etwa 100 kommt niemand.',
        subitise:   'Bis etwa drei oder vier zählst du nicht — du siehst die Anzahl einfach. Danach schaltet das Gehirn auf Einzelzählen um, und die Zeit springt nach oben. Das nennt sich Subitizing, und es haben alle.',
        oddone:     'Unterscheidet sich das eine Element nur in einem Merkmal, springt es dir entgegen, und die Größe des Rasters spielt kaum eine Rolle. Braucht es zwei Merkmale zusammen, musst du Stück für Stück suchen. Gleiches Raster, völlig andere Aufgabe.',
        sequence:   'Das Kurzzeitgedächtnis fasst etwa sieben Einheiten, plus minus zwei — die meistzitierte Zahl der Psychologie. Für Positionen im Raum sind es meist eher fünf.',
        simon:      'Menschen sind rund 30 Millisekunden langsamer, wenn etwas auf der Gegenseite der zuständigen Taste erscheint. Der Ort ist für die Aufgabe egal, und dein Gehirn nutzt ihn trotzdem. Das ist der Simon-Effekt.',
        stroop:     'Lesen läuft so automatisch ab, dass das Wort das Benennen der Farbe stört — die meisten verlieren dabei etwa 100 Millisekunden. Abschalten lässt es sich nicht, und genau deshalb ist es die berühmteste Aufgabe des Fachs.',
        switch:     'Ein Regelwechsel kostet die meisten Menschen ungefähr 200 bis 300 Millisekunden, und zwar jedes Mal. Das ist der wahre Preis von Multitasking: nicht zwei Dinge gleichzeitig, sondern eine Gebühr bei jedem Wechsel.',
        timing:     'Ein bewegtes Intervall einzuschätzen liegt bei fast allen systematisch daneben, und je schneller es läuft, desto stärker driftet es. Dein Körper hält die Zeit deutlich besser als deine Einschätzung davon.',
        blink:      'Erkennst du ein Ziel, kann für etwa die nächsten 200 bis 500 Millisekunden ein zweites unbemerkt vorbeiziehen — auch mitten im Blickfeld. Das heißt Attentional Blink, und es liegt nicht an den Augen.',
        words:      'Das Gehirn speichert den Kern, nicht die Aufnahme. Ein Wort, das zum Thema passt, kann sich genauso erinnert anfühlen wie eines, das wirklich da war — eine falsche Erinnerung, erzeugt von einem gesunden Gedächtnis.'
      }
    },

    /* =============================================================== FR (.fr) */
    /* Calum ruled 2026-08-04: match the local norm — "Faites le test" (Cuure et al).
       "diagnostic" deliberately avoided. "QI" deliberately absent.
       NB: French typography — narrow no-break space (U+202F) before : ; ! ? */
    fr: {
      lang: 'fr-FR',
      // V1 — see uk.barString. `situez-vous` / `vous vous situez` is a deliberate verbatim echo
      // into the landing headline. "Faites le test" is CLOSED — Calum ruled it 2026-08-06 against
      // two independent objections (FR health marketing skews `test` toward `test médical`).
      // Do not re-open the wording. Space before `?` matches this file's existing FR convention.
      barString: 'Où vous situez-vous ? Faites le test ›',
      ui: {
        ready: 'Prêt ?', go: 'PARTEZ',
        tapWhenGreen: 'Touchez dès que ça devient vert',
        tooSoon: 'Trop tôt — attendez le vert',
        tapChanged: 'Touchez la case qui change',
        whichColour: 'Touchez la COULEUR de l’encre, pas le mot',
        whichShape: 'Touchez la FORME que vous voyez, pas le mot',
        repeatSeq: 'Répétez la séquence',
        quickMaths: 'Calcul rapide',
        round: 'Manche', of: 'sur', next: 'Suivant',
        skip: 'Passer cette manche', untimed: 'Sans chronomètre',
        playAgain: 'Rejouer', yourScore: 'Votre score',
        stopInZone: 'Arrêtez le curseur dans la zone jaune', stop: 'STOP',
        oddOneOut: 'Touchez l’intrus',
        byColour: 'PAR COULEUR', byShape: 'PAR FORME',
        countBlue: 'Repérez les BLEUS', howManyBlue: 'Combien de bleus avez-vous vus ?',
        howMany: 'Combien de points ?',
        matchColour: 'Répondez selon la COULEUR — ignorez le côté',
        tapMiddle: 'Touchez le milieu exact de la ligne',
        rememberWords: 'Retenez ces mots', wasItShown: 'Ce mot était-il affiché ?',
        seenIt: 'Je l’ai vu', newWord: 'Nouveau'
      },
      roundNames: {
        reaction: 'Temps de r\u00e9action', subitise: 'Compter les points', oddone: 'L\u2019intrus',
        sequence: 'M\u00e9moire de s\u00e9quence', simon: 'Suivre la couleur', stroop: 'Nommer l\u2019encre',
        timing: 'Arr\u00eater le curseur', words: 'M\u00e9moire des mots',
        switch: 'Changer de r\u00e8gle', blink: 'Compter les bleues'
      },
      howto: {
        reaction: 'Une zone grise devient verte. Touchez-la d\u00e8s qu\u2019elle change \u2014 le plus vite possible. Deux essais.',
        subitise: 'Des points apparaissent bri\u00e8vement. Touchez le nombre que vous avez vu.',
        oddone:   'Un \u00e9l\u00e9ment de la grille est diff\u00e9rent. Touchez-le. Deux grilles \u2014 la premi\u00e8re est facile expr\u00e8s, la seconde non. L\u2019\u00e9cart entre les deux, c\u2019est le r\u00e9sultat.',
        sequence: 'Des cases s\u2019allument dans un ordre. Reproduisez-le. La s\u00e9quence s\u2019allonge \u00e0 chaque r\u00e9ussite et s\u2019arr\u00eate d\u00e8s la premi\u00e8re erreur \u2014 jusqu\u2019o\u00f9 vous allez, C\u2019EST le score.',
        simon:    'Un carr\u00e9 color\u00e9 appara\u00eet \u00e0 gauche ou \u00e0 droite. Touchez le bouton de la m\u00eame COULEUR \u2014 ignorez le c\u00f4t\u00e9.',
        stroop:   'Un nom de couleur appara\u00eet, \u00e9crit dans une encre color\u00e9e. Touchez la COULEUR DE L\u2019ENCRE, pas le mot.',
        timing:   'Un curseur fait des allers-retours. Appuyez sur STOP quand il est dans la zone jaune. Il acc\u00e9l\u00e8re.',
        switch:   'Une forme appara\u00eet. L\u2019\u00e9tiquette au-dessus indique la r\u00e8gle en cours : PAR COULEUR ou PAR FORME. Les deux boutons changent avec la r\u00e8gle.',
        blink:    'Des formes d\u00e9filent tr\u00e8s vite. Repérez les BLEUES — il peut y en avoir 1, 2 ou 3. Comptez combien vous en voyez \u2014 elles peuvent se suivre de tr\u00e8s pr\u00e8s.',
        words:    'Cinq mots apparaissent l\u2019un apr\u00e8s l\u2019autre. Retenez-les. On vous demandera ensuite si certains mots \u00e9taient affich\u00e9s.'
      },
      // No offer exists (removed 2026-08-06).
      offerTerms: null,
      // No reward exists (removed 2026-08-09).
      reward: null,
      percentilePending: 'Nous constituons encore la base de comparaison — voici votre score.',
      percentileOf: 'Plus rapide que {pct}% des {n} personnes ayant déjà joué.',
      percentileFirst: 'Vous êtes la première personne à jouer. Revenez quand quelques autres auront participé — la comparaison prendra alors tout son sens.',
      spreadSlowLabel: 'Plus lent ←',
      spreadFastLabel: '→ Plus rapide',
      scaleScoreTitle: 'Score', scaleSpeedTitle: 'Vitesse',
      scoreLowLabel: 'Plus bas ←', scoreHighLabel: '→ Plus élevé',
      scoreOf: 'Un score plus élevé que {pct}% des {n} personnes ayant déjà joué.',
      // Segue (score -> preference question -> product -> checkout). Shaped to the DOTTED
      // key contract in bz-game-segue.js REQUIRED_STRING_KEYS; that module fails closed on
      // a missing key, so a half-translated market throws instead of rendering a blank
      // button on the highest-intent screen in the flow.
      // Routing is the PREFERENCE answer, never the score — whyThis says so explicitly.
      segue: {
        "intro": "Encore une question — elle n’a rien à voir avec le jeu.",
        "seeMyPick": "Choisir ma formule",
        "question": "Quel genre de journée voulez-vous ?",
        "questionHint": "Choisissez l’un ou l’autre — ou les deux.",
        "pushLabel": "Plus d’élan",
        "pushBody": "Je veux me lancer et tenir la distance.",
        "calmLabel": "Moins de bruit dans la tête",
        "calmBody": "Je veux que ça se calme pour pouvoir réfléchir clairement.",
        "pickPrompt": "Choisissez au moins un pour voir votre formule.",
        "continueCta": "Montrez-moi",
        "changeAnswer": "Changer ma réponse",
        "pickHeading": "Votre choix",
        "whyThis": "Cela découle de votre choix — de rien d’autre.",
        "buyOnce": "Achat unique",
        "buyOnceNote": "Une commande ponctuelle.",
        "subscribe": "S’abonner et économiser",
        "subscribeNote": "Suspendez ou annulez à tout moment.",
        "products": {
          "pro": {
            "tier": "motivation",
            "line": "Concentration forte et motivation pour les journées qui demandent de l’élan."
          },
          "elite": {
            "tier": "anti-stress",
            "line": "Concentration sans stress et humeur positive quand tout devient bruyant."
          },
          "bundle": {
            "tier": "les trois formules",
            "line": "Une de chaque — essayez les trois et trouvez la vôtre."
          }
        }
      },
      // The player's OWN number, prepended to the science card. Localised because it is
      // customer-facing prose, not a debug label — it used to be hardcoded English.
      stat: {
        reaction:   '{ms} ms.',
        stroop:     '{ms} ms de plus quand le mot luttait contre l’encre.',
        sequence:   '{n} dans l’ordre.',
        simon:      '{ms} ms de plus en cas de conflit.',
        switch:     '{ms} ms perdues à chaque changement de règle.',
        timing:     'À {pct}% du parcours près.',
        bisect:     'À {pct}% du centre.',
        subitise:   '{pct}% de réussite.',
        oddone:     '{pct}% de réussite.',
        blink:      '{pct}% de réussite.',
        words:      '{pct}% de réussite.'
      },
      science: {
        // COMPARATIVE cards (Calum 2026-08-07). Each names the PUBLISHED effect size so the
        // player's own number, prepended by measuredStat(), has something real to sit against.
        // Every figure is an established, replicated finding, hedged because these effects have
        // ranges rather than point values. The insight stays in the PHENOMENON — the player
        // shares the effect, they are never ranked by it.
        reaction:   'Une réaction simple prend à la plupart des gens environ 250 millisecondes, et presque rien là-dedans n’est de la réflexion — le signal doit traverser l’œil, le cerveau et le pouce. Personne ne descend sous 100 environ.',
        subitise:   'Jusqu’à trois ou quatre environ, vous ne comptez pas — vous voyez la quantité. Au-delà, le cerveau passe au comptage un par un et le temps grimpe. Cela s’appelle la subitisation, et tout le monde l’a.',
        oddone:     'Quand l’intrus ne diffère que par un seul trait, il saute aux yeux et la taille de la grille importe peu. Quand il faut deux traits combinés, il faut chercher élément par élément. Même grille, tâche totalement différente.',
        sequence:   'La mémoire à court terme retient environ sept éléments, à deux près — le chiffre le plus cité de la psychologie. Pour des positions dans l’espace, on est plutôt autour de cinq.',
        simon:      'On est environ 30 millisecondes plus lent quand un élément apparaît du côté opposé au bouton qui lui correspond. Le côté n’a aucune importance pour la tâche, et votre cerveau s’en sert quand même. C’est l’effet Simon.',
        stroop:     'La lecture est si automatique que le mot gêne la dénomination de l’encre — la plupart des gens y perdent environ 100 millisecondes. Impossible de la désactiver, et c’est précisément pour cela que c’est la tâche la plus célèbre du domaine.',
        switch:     'Changer de règle coûte à la plupart des gens de l’ordre de 200 à 300 millisecondes, à chaque fois. C’est le vrai prix du multitâche : non pas faire deux choses à la fois, mais payer un péage à chaque bascule.',
        timing:     'Estimer un intervalle en mouvement est systématiquement décalé chez presque tout le monde, et plus cela va vite, plus cela dérive. Votre corps mesure le temps bien mieux que votre estimation.',
        blink:      'Repérez une cible et, pendant environ 200 à 500 millisecondes, une seconde peut passer sans être vue — même en pleine vue. Cela s’appelle le clignement attentionnel, et ce ne sont pas vos yeux.',
        words:      'Le cerveau garde l’essentiel, pas l’enregistrement. Un mot qui colle au thème peut sembler aussi mémorisé qu’un mot réellement vu — un faux souvenir produit par une mémoire saine qui fonctionne normalement.'
      }
    }
  };

  /* ---------------------------------------------------------- runtime guards */
  var LOCKED_TERMS = ['nootropic', 'nootropikum', 'nootropique', 'nootropics'];
  // Claim-ish vocabulary that must never appear in GAME copy on any market.
  var BANNED_CLAIMY = ['treat', 'cure', 'prevent', 'diagnos', 'symptom', 'deficien', 'disorder',
                       'heilt', 'behandelt', 'diagnos', 'guérit', 'traite', 'soigne'];

  function walkStrings(obj, visit, path) {
    path = path || '';
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      if (v == null) continue;
      if (typeof v === 'string') visit(v, path + '.' + k);
      else if (typeof v === 'object') walkStrings(v, visit, path + '.' + k);
    }
  }

  /** Fail-closed at load: a locked or claim-ish term anywhere in a market's strings throws. */
  function assertNoLockedTerms(market) {
    var set = I18N[market];
    if (!set) throw new Error('bz-game-i18n: unknown market "' + market + '"');
    walkStrings(set, function (s, where) {
      var low = s.toLowerCase();
      LOCKED_TERMS.forEach(function (t) {
        if (low.indexOf(t) !== -1) {
          throw new Error('bz-game-i18n: LOCKED term "' + t + '" in ' + market + where);
        }
      });
      BANNED_CLAIMY.forEach(function (t) {
        if (low.indexOf(t) !== -1) {
          throw new Error('bz-game-i18n: claim vocabulary "' + t + '" in ' + market + where);
        }
      });
      if (root.BZGame && root.BZGame.assertNoConnective) root.BZGame.assertNoConnective(s);
    });
    return true;
  }

  function get(market) {
    assertNoLockedTerms(market);
    return I18N[market];
  }

  root.BZGameI18N = { markets: I18N, get: get, assertNoLockedTerms: assertNoLockedTerms };
})(window);
